// src/admin/pages/SettingsPage.js
import React, { useState, useEffect } from 'react';
import { 
  Save,
  AlertCircle,
  Plus,
  Trash2,
  Edit,
  Bell,
  X,
  Settings,
  CheckCircle
} from 'lucide-react';
import { supabase } from '../../config';

const SettingsPage = () => {
  const [activeTab, setActiveTab] = useState('system'); // Default to System tab
  const [settings, setSettings] = useState({});
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingMessage, setEditingMessage] = useState(null);
  const [inlineMessage, setInlineMessage] = useState(null); // { type: 'success' | 'error', text: string }

  // Load settings from Supabase
  const loadSettings = async () => {
    setLoading(true);
    try {
      // Fetch platform settings
      const { data: settingsData, error: settingsError } = await supabase
        .from('platform_settings')
        .select('*')
        .single();

      if (!settingsError && settingsData) {
        setSettings(settingsData.settings || {});
      } else {
        // Default settings
        setSettings({
          platform_name: 'Kapitalized',
          default_currency: 'USD',
          default_country: 'US',
          timezone: 'UTC',
          date_format: 'MM/DD/YYYY',
          session_timeout: 30,
          password_min_length: 8,
          require_2fa_admin: false,
          max_login_attempts: 5,
          enable_equity_manager: true,
          enable_dataroom: true,
          enable_capraise: true,
          maintenance_mode: false,
          maintenance_message: 'System under maintenance. We\'ll be back soon!',
          log_level: 'warning',
          enable_audit_trail: true,
          auto_backup_enabled: false,
          backup_frequency: 'weekly'
        });
      }

      // Fetch user messages
      const { data: messagesData, error: messagesError } = await supabase
        .from('platform_messages')
        .select('*')
        .order('created_at', { ascending: false });

      if (!messagesError && messagesData) {
        setMessages(messagesData);
      }
    } catch (error) {
      console.error('Error loading settings:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSettings();
  }, []);

  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      const { error } = await supabase
        .from('platform_settings')
        .upsert({
          id: 1,
          settings: settings,
          updated_at: new Date().toISOString()
        });

      if (error) throw error;

      showMessage('success', 'Settings saved successfully!');
    } catch (error) {
      console.error('Error saving settings:', error);
      showMessage('error', 'Error saving settings: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveMessage = async (message) => {
    try {
      // Clean up empty date strings - convert to null
      const cleanedMessage = {
        ...message,
        start_date: message.start_date && message.start_date.trim() !== '' ? message.start_date : null,
        end_date: message.end_date && message.end_date.trim() !== '' ? message.end_date : null
      };

      if (cleanedMessage.id) {
        // Update existing - exclude id, created_at, and updated_at (these are managed by the database)
        const { id, created_at, updated_at, ...updateData } = cleanedMessage;
        const { error } = await supabase
          .from('platform_messages')
          .update(updateData)
          .eq('id', id);

        if (error) throw error;
      } else {
        // Create new - exclude id and created_at (id is auto-generated, created_at will be set by default)
        const { id, created_at, ...insertData } = cleanedMessage;
        const { error } = await supabase
          .from('platform_messages')
          .insert([insertData]);

        if (error) throw error;
      }

      setEditingMessage(null);
      loadSettings();
      showMessage('success', 'Message saved successfully!');
    } catch (error) {
      console.error('Error saving message:', error);
      showMessage('error', 'Error saving message: ' + error.message);
    }
  };

  const handleDeleteMessage = async (id) => {
    if (!window.confirm('Delete this message?')) return;

    try {
      const { error } = await supabase
        .from('platform_messages')
        .delete()
        .eq('id', id);

      if (error) throw error;

      loadSettings();
      showMessage('success', 'Message deleted successfully!');
    } catch (error) {
      console.error('Error deleting message:', error);
      showMessage('error', 'Error deleting message: ' + error.message);
    }
  };

  const updateSetting = (key, value) => {
    setSettings({ ...settings, [key]: value });
  };

  const showMessage = (type, text) => {
    setInlineMessage({ type, text });
    // Auto-dismiss after 5 seconds
    setTimeout(() => {
      setInlineMessage(null);
    }, 5000);
  };

  if (loading) {
    return (
      <div className="w-full max-w-full">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading settings...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8">
      {/* Inline Message */}
      {inlineMessage && (
        <div className={`mb-6 p-4 rounded-lg border-l-4 flex items-center justify-between ${
          inlineMessage.type === 'success' 
            ? 'bg-green-50 border-green-400 text-green-800' 
            : 'bg-red-50 border-red-400 text-red-800'
        }`}>
          <div className="flex items-center">
            {inlineMessage.type === 'success' ? (
              <CheckCircle className="h-5 w-5 mr-3" />
            ) : (
              <AlertCircle className="h-5 w-5 mr-3" />
            )}
            <p className="font-medium">{inlineMessage.text}</p>
          </div>
          <button
            onClick={() => setInlineMessage(null)}
            className="ml-4 text-gray-400 hover:text-gray-600"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
      )}

      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Platform Settings</h2>
          <p className="text-gray-600 mt-1">Configure platform-wide settings and preferences</p>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={handleSaveSettings}
            disabled={saving}
            className="flex items-center px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors disabled:opacity-50"
          >
            <Save className="h-4 w-4 mr-2" />
            {saving ? 'Saving...' : 'Save All Settings'}
          </button>
        </div>
      </div>

      {/* Tab Navigation - Simplified */}
      <div className="bg-white rounded-lg shadow mb-6">
        <div className="border-b border-gray-200">
          <nav className="flex flex-wrap -mb-px">
            {[
              { id: 'system', label: 'System', icon: Settings }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center px-6 py-3 text-sm font-medium ${
                  activeTab === tab.id
                    ? 'border-b-2 border-blue-500 text-blue-600'
                    : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <tab.icon className="h-4 w-4 mr-2" />
                {tab.label}
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Settings Content */}
      <div className="bg-white rounded-lg shadow p-6">
        
        {/* System Settings - Sitewide Message and Maintenance Mode */}
        {activeTab === 'system' && (
          <div className="space-y-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">System Settings</h3>
            
            {/* Sitewide Message */}
            <div className="border-b pb-6">
              <h4 className="text-md font-semibold text-gray-900 mb-4">Sitewide Message</h4>
              <div className="flex justify-between items-center mb-4">
                <p className="text-sm text-gray-600">Display a message to all users across the platform</p>
                <button
                  onClick={() => setEditingMessage({
                    message: '',
                    type: 'info',
                    module: 'all',
                    zone: 'zone1',
                    start_date: new Date().toISOString().split('T')[0],
                    end_date: '',
                    is_active: true
                  })}
                  className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  New Message
                </button>
              </div>

              {messages.length === 0 ? (
                <div className="text-center py-8 text-gray-500 border-2 border-dashed border-gray-300 rounded-lg">
                  <Bell className="h-12 w-12 mx-auto mb-2 text-gray-400" />
                  <p>No messages configured</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {messages.map((msg) => (
                    <div key={msg.id} className={`p-4 rounded-lg border-l-4 ${
                      msg.type === 'error' ? 'bg-red-50 border-red-500' :
                      msg.type === 'warning' ? 'bg-yellow-50 border-yellow-500' :
                      msg.type === 'success' ? 'bg-green-50 border-green-500' :
                      'bg-blue-50 border-blue-500'
                    }`}>
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className={`px-2 py-0.5 text-xs rounded ${
                              msg.type === 'error' ? 'bg-red-100 text-red-800' :
                              msg.type === 'warning' ? 'bg-yellow-100 text-yellow-800' :
                              msg.type === 'success' ? 'bg-green-100 text-green-800' :
                              'bg-blue-100 text-blue-800'
                            }`}>
                              {msg.type}
                            </span>
                            <span className="text-xs text-gray-600">
                              Module: {msg.module}
                            </span>
                            <span className="text-xs text-gray-600">
                              Zone: {msg.zone || 'zone1'}
                            </span>
                            <span className={`text-xs ${msg.is_active ? 'text-green-600' : 'text-gray-400'}`}>
                              {msg.is_active ? '● Active' : '○ Inactive'}
                            </span>
                          </div>
                          <p className="text-sm text-gray-900 mb-1">{msg.message}</p>
                          <p className="text-xs text-gray-600">
                            {msg.start_date && (() => {
                              try {
                                const date = new Date(msg.start_date);
                                if (isNaN(date.getTime())) return 'Start: N/A';
                                const day = date.getDate();
                                const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
                                const month = monthNames[date.getMonth()];
                                const year = date.getFullYear();
                                return `Start: ${day} ${month} ${year}`;
                              } catch {
                                return 'Start: N/A';
                              }
                            })()}
                            {msg.end_date && ` • ` + (() => {
                              try {
                                const date = new Date(msg.end_date);
                                if (isNaN(date.getTime())) return 'End: N/A';
                                const day = date.getDate();
                                const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
                                const month = monthNames[date.getMonth()];
                                const year = date.getFullYear();
                                return `End: ${day} ${month} ${year}`;
                              } catch {
                                return 'End: N/A';
                              }
                            })()}
                          </p>
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={() => setEditingMessage(msg)}
                            className="p-1 text-blue-600 hover:bg-blue-100 rounded"
                          >
                            <Edit className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteMessage(msg.id)}
                            className="p-1 text-red-600 hover:bg-red-100 rounded"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Maintenance Mode */}
            <div className="pt-6">
              <h4 className="text-md font-semibold text-gray-900 mb-4">Maintenance Mode</h4>
              
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
                <div className="flex items-start">
                  <AlertCircle className="h-5 w-5 text-amber-600 mt-0.5 mr-3" />
                  <div>
                    <p className="text-sm font-medium text-amber-800">Warning</p>
                    <p className="text-sm text-amber-700 mt-1">
                      Enabling maintenance mode will make the platform inaccessible to all users except super admins.
                    </p>
                  </div>
                </div>
              </div>

              <label className="flex items-center p-4 bg-gray-50 rounded-lg">
                <input
                  type="checkbox"
                  checked={settings.maintenance_mode || false}
                  onChange={(e) => updateSetting('maintenance_mode', e.target.checked)}
                  className="h-5 w-5 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <span className="ml-3 text-sm font-medium text-gray-700">
                  Enable Maintenance Mode
                </span>
              </label>

              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Maintenance Message</label>
                <textarea
                  value={settings.maintenance_message || ''}
                  onChange={(e) => updateSetting('maintenance_message', e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="This message will be displayed to users when maintenance mode is enabled"
                />
              </div>
            </div>
          </div>
        )}

      </div>

      {/* Message Editor Modal */}
      {editingMessage && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-2xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  {editingMessage.id ? 'Edit Message' : 'New Message'}
                </h3>
                <button
                  onClick={() => setEditingMessage(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Message Text</label>
                  <textarea
                    value={editingMessage.message}
                    onChange={(e) => setEditingMessage({ ...editingMessage, message: e.target.value })}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter message to display to users"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Message Type</label>
                    <select
                      value={editingMessage.type}
                      onChange={(e) => setEditingMessage({ ...editingMessage, type: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="info">Info (Blue)</option>
                      <option value="success">Success (Green)</option>
                      <option value="warning">Warning (Yellow)</option>
                      <option value="error">Error (Red)</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Display Zone</label>
                    <select
                      value={editingMessage.zone || 'zone1'}
                      onChange={(e) => setEditingMessage({ ...editingMessage, zone: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="zone1">Zone 1 - Bar on top of header (sitewide)</option>
                      <option value="zone2">Zone 2 - Below my account header</option>
                      <option value="zone3">Zone 3 - Login page</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Display In Module</label>
                  <select
                    value={editingMessage.module}
                    onChange={(e) => setEditingMessage({ ...editingMessage, module: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="all">All Modules</option>
                    <option value="equity_manager">Equity Manager</option>
                    <option value="dataroom">Dataroom</option>
                    <option value="capraise">Capital Raising</option>
                    <option value="admin">Admin Only</option>
                  </select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Start Date</label>
                    <input
                      type="date"
                      value={editingMessage.start_date || ''}
                      onChange={(e) => setEditingMessage({ ...editingMessage, start_date: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">End Date (Optional)</label>
                    <input
                      type="date"
                      value={editingMessage.end_date || ''}
                      onChange={(e) => setEditingMessage({ ...editingMessage, end_date: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>

                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={editingMessage.is_active !== false}
                    onChange={(e) => setEditingMessage({ ...editingMessage, is_active: e.target.checked })}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <span className="ml-2 text-sm text-gray-700">Active (Display this message)</span>
                </label>
              </div>

              <div className="mt-6 flex justify-end space-x-3">
                <button
                  onClick={() => setEditingMessage(null)}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleSaveMessage(editingMessage)}
                  className="flex items-center px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
                >
                  <Save className="h-4 w-4 mr-2" />
                  Save Message
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SettingsPage;

